import api from './api';

// Integrity API - SHA-256 Digital Seal Engine
export const integrityApi = {
  createSeal: async (data: {
    record_type: 'mission' | 'analysis' | 'decision' | 'user_action';
    record_id: string;
    data: any;
  }) => {
    const response = await api.post('/integrity/seals', data);
    return response.data;
  },

  getSeal: async (recordType: string, recordId: string) => {
    const response = await api.get(`/integrity/seals/${recordType}/${recordId}`);
    return response.data;
  },

  verifySeal: async (recordType: string, recordId: string, currentData: any) => {
    const response = await api.post(`/integrity/verify/${recordType}/${recordId}`, { currentData });
    return response.data;
  },

  verifyChain: async () => {
    const response = await api.get('/integrity/chain/verify');
    return response.data;
  },

  getTamperAlerts: async (params?: { limit?: number; offset?: number }) => {
    const response = await api.get('/integrity/tamper-alerts', { params });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/integrity/stats');
    return response.data;
  },
};

// Audit API - AI Decision Audit Trail
export const auditApi = {
  getDecision: async (decisionId: string) => {
    const response = await api.get(`/audit/decisions/${decisionId}`);
    return response.data;
  },

  getMissionDecisions: async (missionId: string, params?: { limit?: number; offset?: number }) => {
    const response = await api.get(`/audit/missions/${missionId}/decisions`, { params });
    return response.data;
  },

  getAllDecisions: async (params?: {
    limit?: number;
    offset?: number;
    decision_type?: string;
    model_used?: string;
    min_confidence?: number;
    start_date?: string;
    end_date?: string;
  }) => {
    // Backend controller reads camelCase query params
    const mapped: Record<string, any> = {};
    if (params?.limit !== undefined)       mapped.limit          = params.limit;
    if (params?.offset !== undefined)      mapped.offset         = params.offset;
    if (params?.decision_type)             mapped.decisionType   = params.decision_type;
    if (params?.model_used)                mapped.aiModel        = params.model_used;
    if (params?.min_confidence !== undefined) mapped.minConfidence = params.min_confidence;
    if (params?.start_date)                mapped.startDate      = params.start_date;
    if (params?.end_date)                  mapped.endDate        = params.end_date;
    const response = await api.get('/audit/decisions', { params: mapped });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/audit/stats');
    return response.data;
  },

  getReasoningTree: async (decisionId: string) => {
    const response = await api.get(`/audit/decisions/${decisionId}/reasoning-tree`);
    return response.data;
  },

  exportAudit: async (params: { start_date: string; end_date: string; format?: 'json' | 'csv' }) => {
    const response = await api.post('/audit/export', {
      startDate: params.start_date,
      endDate: params.end_date,
      format: params.format ?? 'json',
    });
    return response.data;
  },
};

// HITL API - Human-in-the-Loop Override Framework
export const hitlApi = {
  certifyDecision: async (data: {
    decision_id: string;
    reason: string;
    analyst_notes?: string;
  }) => {
    const response = await api.post('/hitl/certify', data);
    return response.data;
  },

  rejectDecision: async (data: {
    decision_id: string;
    reason: string;
    analyst_notes?: string;
  }) => {
    const response = await api.post('/hitl/reject', data);
    return response.data;
  },

  modifyDecision: async (data: {
    decision_id: string;
    reason: string;
    modified_result: any;
    analyst_notes?: string;
  }) => {
    const response = await api.post('/hitl/modify', data);
    return response.data;
  },

  getOverride: async (overrideId: string) => {
    const response = await api.get(`/hitl/overrides/${overrideId}`);
    return response.data;
  },

  getAllOverrides: async (params?: {
    limit?: number;
    offset?: number;
    action?: 'certify' | 'reject' | 'modify';
    analyst_id?: string;
    start_date?: string;
    end_date?: string;
  }) => {
    const response = await api.get('/hitl/overrides', { params });
    return response.data;
  },

  getPendingDecisions: async (params?: { limit?: number; offset?: number }) => {
    const response = await api.get('/hitl/pending', { params });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/hitl/stats');
    return response.data;
  },

  getAnalystMetrics: async (analystId?: string) => {
    const response = await api.get(`/hitl/analyst-metrics${analystId ? `/${analystId}` : ''}`);
    return response.data;
  },

  getComplianceReport: async (params: { start_date: string; end_date: string }) => {
    const response = await api.get('/hitl/compliance-report', { params });
    return response.data;
  },
};

// MFA API - Multi-Factor Authentication
export const mfaApi = {
  setupTOTP: async () => {
    const response = await api.post('/mfa/totp/setup');
    return response.data;
  },

  verifyTOTP: async (data: { token: string }) => {
    const response = await api.post('/mfa/totp/verify', data);
    return response.data;
  },

  disableTOTP: async (data: { token: string }) => {
    const response = await api.post('/mfa/totp/disable', data);
    return response.data;
  },

  setupSMS: async (data: { phone_number: string }) => {
    const response = await api.post('/mfa/sms/setup', data);
    return response.data;
  },

  verifySMS: async (data: { code: string }) => {
    const response = await api.post('/mfa/sms/verify', data);
    return response.data;
  },

  disableSMS: async () => {
    const response = await api.post('/mfa/sms/disable');
    return response.data;
  },

  setupEmail: async () => {
    const response = await api.post('/mfa/email/setup');
    return response.data;
  },

  verifyEmail: async (data: { code: string }) => {
    const response = await api.post('/mfa/email/verify', data);
    return response.data;
  },

  disableEmail: async () => {
    const response = await api.post('/mfa/email/disable');
    return response.data;
  },

  getBackupCodes: async () => {
    const response = await api.get('/mfa/backup-codes');
    return response.data;
  },

  regenerateBackupCodes: async () => {
    const response = await api.post('/mfa/backup-codes/regenerate');
    return response.data;
  },

  getStatus: async () => {
    const response = await api.get('/mfa/status');
    return response.data;
  },
};

// Hierarchy API - Organizational Hierarchy Management
export const hierarchyApi = {
  createNode: async (data: {
    name: string;
    node_type: string;
    parent_id?: string;
    config?: any;
    boundary?: any;
  }) => {
    const response = await api.post('/hierarchy/nodes', data);
    return response.data;
  },

  getNode: async (nodeId: string) => {
    const response = await api.get(`/hierarchy/nodes/${nodeId}`);
    return response.data;
  },

  updateNode: async (nodeId: string, data: any) => {
    const response = await api.put(`/hierarchy/nodes/${nodeId}`, data);
    return response.data;
  },

  deleteNode: async (nodeId: string) => {
    const response = await api.delete(`/hierarchy/nodes/${nodeId}`);
    return response.data;
  },

  getChildren: async (nodeId: string, params?: { limit?: number; offset?: number }) => {
    const response = await api.get(`/hierarchy/nodes/${nodeId}/children`, { params });
    return response.data;
  },

  getAncestors: async (nodeId: string) => {
    const response = await api.get(`/hierarchy/nodes/${nodeId}/ancestors`);
    return response.data;
  },

  getDescendants: async (nodeId: string, params?: { max_depth?: number }) => {
    const response = await api.get(`/hierarchy/nodes/${nodeId}/descendants`, { params });
    return response.data;
  },

  getRootNodes: async (params?: { limit?: number; offset?: number }) => {
    const response = await api.get('/hierarchy/roots', { params });
    return response.data;
  },

  searchNodes: async (params: { query: string; node_type?: string; limit?: number }) => {
    const response = await api.get('/hierarchy/search', { params });
    return response.data;
  },

  getNodesByBoundary: async (data: { boundary: any; limit?: number }) => {
    const response = await api.post('/hierarchy/nodes/by-boundary', data);
    return response.data;
  },

  getTree: async () => {
    const response = await api.get('/hierarchy/tree');
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/hierarchy/stats');
    return response.data;
  },

  moveNode: async (nodeId: string, newParentId: string | null) => {
    const response = await api.post(`/hierarchy/nodes/${nodeId}/move`, {
      new_parent_id: newParentId,
    });
    return response.data;
  },
};

// Modules API - Module Entitlement & Provisioning
export const modulesApi = {
  getAllModules: async () => {
    const response = await api.get('/modules');
    return response.data;
  },

  getModule: async (moduleId: string) => {
    const response = await api.get(`/modules/${moduleId}`);
    return response.data;
  },

  getActiveModules: async () => {
    const response = await api.get('/modules/active');
    return response.data;
  },

  activateModule: async (data: { module_id: string; tier: string; config?: any }) => {
    const response = await api.post('/modules/activate', data);
    return response.data;
  },

  deactivateModule: async (moduleId: string) => {
    const response = await api.post(`/modules/${moduleId}/deactivate`);
    return response.data;
  },

  updateModuleConfig: async (moduleId: string, data: { config: any }) => {
    const response = await api.put(`/modules/${moduleId}/config`, data);
    return response.data;
  },

  getUsage: async (moduleId: string, params?: { start_date?: string; end_date?: string }) => {
    const response = await api.get(`/modules/${moduleId}/usage`, { params });
    return response.data;
  },

  getAllUsage: async (params?: { start_date?: string; end_date?: string }) => {
    const response = await api.get('/modules/usage', { params });
    return response.data;
  },

  checkAccess: async (data: { module_id: string; feature: string }) => {
    const response = await api.post('/modules/check-access', data);
    return response.data;
  },

  getQuotas: async () => {
    const response = await api.get('/modules/quotas');
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/modules/stats');
    return response.data;
  },
};

// Notifications API - Event-Driven Notifications
export const notificationsApi = {
  send: async (data: {
    type: string;
    title: string;
    message: string;
    channels: string[];
    priority?: string;
    data?: any;
  }) => {
    const response = await api.post('/notifications/send', data);
    return response.data;
  },

  getAll: async (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    type?: string;
    channel?: string;
  }) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  getById: async (notificationId: string) => {
    const response = await api.get(`/notifications/${notificationId}`);
    return response.data;
  },

  markAsRead: async (notificationId: string) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },

  getPreferences: async () => {
    const response = await api.get('/notifications/preferences');
    return response.data;
  },

  updatePreferences: async (data: {
    email_enabled?: boolean;
    sms_enabled?: boolean;
    push_enabled?: boolean;
    webhook_enabled?: boolean;
    quiet_hours?: { start: string; end: string };
  }) => {
    const response = await api.put('/notifications/preferences', data);
    return response.data;
  },

  createAlertRule: async (data: {
    name: string;
    event_type: string;
    conditions: any;
    channels: string[];
    cooldown_minutes?: number;
  }) => {
    const response = await api.post('/notifications/alert-rules', data);
    return response.data;
  },

  getAlertRules: async () => {
    const response = await api.get('/notifications/alert-rules');
    return response.data;
  },

  deleteAlertRule: async (ruleId: string) => {
    const response = await api.delete(`/notifications/alert-rules/${ruleId}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/notifications/stats');
    return response.data;
  },
};

// Webhooks API - Webhook Integration with DLQ
export const webhooksApi = {
  create: async (data: {
    name: string;
    url: string;
    events: string[];
    secret?: string;
    retry_config?: any;
  }) => {
    const response = await api.post('/webhooks', data);
    return response.data;
  },

  getAll: async (params?: { limit?: number; offset?: number; status?: string }) => {
    const response = await api.get('/webhooks', { params });
    return response.data;
  },

  getById: async (webhookId: string) => {
    const response = await api.get(`/webhooks/${webhookId}`);
    return response.data;
  },

  update: async (webhookId: string, data: any) => {
    const response = await api.put(`/webhooks/${webhookId}`, data);
    return response.data;
  },

  delete: async (webhookId: string) => {
    const response = await api.delete(`/webhooks/${webhookId}`);
    return response.data;
  },

  test: async (webhookId: string, data?: { payload: any }) => {
    const response = await api.post(`/webhooks/${webhookId}/test`, data);
    return response.data;
  },

  getDeliveries: async (webhookId: string, params?: { limit?: number; offset?: number; status?: string }) => {
    const response = await api.get(`/webhooks/${webhookId}/deliveries`, { params });
    return response.data;
  },

  retryDelivery: async (webhookId: string, deliveryId: string) => {
    const response = await api.post(`/webhooks/${webhookId}/deliveries/${deliveryId}/retry`);
    return response.data;
  },

  subscribe: async (data: { webhook_id: string; event_type: string }) => {
    const response = await api.post('/webhooks/subscriptions', data);
    return response.data;
  },

  unsubscribe: async (webhookId: string, eventType: string) => {
    const response = await api.delete(`/webhooks/${webhookId}/subscriptions/${eventType}`);
    return response.data;
  },

  getStats: async (webhookId?: string) => {
    const url = webhookId ? `/webhooks/${webhookId}/stats` : '/webhooks/stats';
    const response = await api.get(url);
    return response.data;
  },

  getDLQ: async (params?: { limit?: number; offset?: number }) => {
    const response = await api.get('/webhooks/dlq', { params });
    return response.data;
  },

  retryDLQ: async (itemId: string) => {
    const response = await api.post(`/webhooks/dlq/${itemId}/retry`);
    return response.data;
  },
};

export default {
  integrity: integrityApi,
  audit: auditApi,
  hitl: hitlApi,
  mfa: mfaApi,
  hierarchy: hierarchyApi,
  modules: modulesApi,
  notifications: notificationsApi,
  webhooks: webhooksApi,
};

// Made with Bob
